/**
 * Sidebar component
 * Navigation sidebar with menu items
 */

import { Layout, Menu, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  SettingOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  HeartFilled,
  ClockCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSiteAuth } from '@/stores/useSiteAuthStore';

const { Sider } = Layout;
const { Text } = Typography;

type MenuItem = Required<MenuProps>['items'][number];

const allMenuItems: MenuItem[] = [
  {
    key: '/',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: '/jobs/new',
    icon: <PlusOutlined />,
    label: 'New Job',
  },
  {
    key: '/jobs',
    icon: <UnorderedListOutlined />,
    label: 'Jobs',
  },
  {
    key: '/schedules',
    icon: <ClockCircleOutlined />,
    label: 'Schedules',
  },
  {
    key: '/results',
    icon: <EnvironmentOutlined />,
    label: 'Results',
  },
  {
    key: '/social-lookup',
    icon: <SearchOutlined />,
    label: 'Social Lookup',
  },
  {
    key: '/maps',
    icon: <GlobalOutlined />,
    label: 'Maps',
  },
  {
    type: 'divider',
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: 'Settings',
  },
  {
    key: '/stats',
    icon: <BarChartOutlined />,
    label: 'Stats',
  },
];

const restrictedMenuItems: MenuItem[] = [
  {
    key: '/results',
    icon: <EnvironmentOutlined />,
    label: 'Results',
  },
  {
    key: '/social-lookup',
    icon: <SearchOutlined />,
    label: 'Social Lookup',
  },
];

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useSiteAuth();
  const menuItems = isAdmin ? allMenuItems : restrictedMenuItems;

  // Determine active menu key
  const getSelectedKey = () => {
    const path = location.pathname;
    // Handle job detail routes
    if (path.startsWith('/jobs/') && path !== '/jobs/new') {
      return '/jobs';
    }
    // Handle schedule sub-routes
    if (path.startsWith('/schedules/')) {
      return '/schedules';
    }
    return path;
  };

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  return (
    <Sider
      theme="dark"
      width={240}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
      }}
    >
      {/* Logo */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <DatabaseOutlined
          style={{
            fontSize: 24,
            color: '#1677ff',
          }}
        />
        <Text
          strong
          style={{
            color: '#fff',
            fontSize: 16,
            marginLeft: 12,
            whiteSpace: 'nowrap',
          }}
        >
          Maps Scraper
        </Text>
      </div>

      {/* Navigation menu */}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderRight: 0,
          marginTop: 8,
        }}
      />

      {/* Footer */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 20px',
          background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.3) 100%)',
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              color: 'rgba(255, 255, 255, 0.45)',
              fontSize: 11,
              letterSpacing: 0.5,
            }}
          >
            Made with{' '}
            <HeartFilled
              style={{
                color: '#ff4d4f',
                fontSize: 10,
                margin: '0 4px',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            />{' '}
            for data
          </Text>
        </div>
      </div>

      {/* Add pulse animation via style tag */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
      `}</style>
    </Sider>
  );
}

export default Sidebar;
